import { IProduct } from "@/api/AppApi/services/home/interfaces/IProduct";
import { memo, useMemo } from "react";
import ProductCard from "./product-box-helpers/ProductCard";
import ProductBoxImage from "./product-box-helpers/product-box-image/ProductBoxImage";
import ProductName from "./product-box-helpers/ProductName";
import ProductDescription from "./product-box-helpers/ProductDescription";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import ProductBrand from "./product-box-helpers/ProductBrand";
import ProductClick from "./product-box-helpers/ProductClick";
import { Config_Customize } from "@/api/AppApi/services/home/interfaces/ICategory";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import ProductBoxTailAndRate from "./ProductBoxTailAndRate";
import dynamic from "next/dynamic";
const EventCustomData = dynamic(() => import("./product-box-helpers/event/EventCustomData"));
import StockAlertMessage from "./product-box-helpers/stock-alert-message";
import { ProductPreviewTypes } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
interface Props extends IProduct {
  scrollableTabs: boolean;
  size: Size;
  theme: ITheme;
  priority: boolean;
  Customize?: Config_Customize;
  isGrid: boolean;
  showCustomEventData?: boolean;
  hideAddToCart?: boolean;
  showAllAttributes?: boolean;
  bundleSetSelectedChildIndex?: (index: number | null) => void;
  bundleItemQuantity?: number;
}

const ProductBox = (props: Props) => {
  const {
    theme,
    size,
    scrollableTabs,
    isGrid,
    Customize,
    discount,
    slug,
    is_new,
    has_discount,
    discount_amount,
    discount_type,
    priority,
    name,
    description,
    brand,
    images,
    preview_type,
    custom_data,
    price,
    price_without_discount,
    selling_prices,
    showCustomEventData,
    has_required_addons,
    has_term_and_condition,
    has_required_fields,
    quantity_on_hand,
    sold_quantity,
    tag_ids,
  } = props;

  const {
    show_brand,
    show_description,
    show_name,
    show_low_stock_alert,
    min_stock_quantity_to_show_alert,
  } = theme?.components?.product ?? {};

  const showMinStockQuantityMsg = useMemo(
    () =>
      show_low_stock_alert &&
      quantity_on_hand <= min_stock_quantity_to_show_alert &&
      quantity_on_hand > 0,
    [show_low_stock_alert, quantity_on_hand, min_stock_quantity_to_show_alert],
  );

  const minimizedProduct = useMemo(
    () => ({
      id: props.id,
      quantity_on_hand: props.quantity_on_hand,
      is_disable: props.is_disable,
      min_quantity_in_cart: props.min_quantity_in_cart,
      max_quantity_in_cart: props.max_quantity_in_cart,
      is_configurable: props.is_configurable,
      has_configurable_parent: props.has_configurable_parent,
      attribute_ids: props.attribute_ids ?? [],
      attributes_builder: props.attributes_builder,
      children: props.children,
      selling_prices: props.selling_prices,
      discount_amount,
      discount_type: discount_type!,
      discount: discount!,
      is_new,
      images,
      price,
      price_without_discount,
      has_required_addons,
      has_term_and_condition,
      has_required_fields,
      has_discount,
      sold_quantity,
      is_favorite: props.is_favorite,
      slug,
      unit_id: Number(props.unit_id),
    }),
    [
      props.id,
      props.quantity_on_hand,
      props.is_disable,
      props.min_quantity_in_cart,
      props.max_quantity_in_cart,
      props.is_configurable,
      props.has_configurable_parent,
      props.attribute_ids,
      props.attributes_builder,
      props.children,
      props.selling_prices,
      discount_amount,
      discount_type,
      discount,
      is_new,
      images,
      price,
      price_without_discount,
      has_required_addons,
      has_term_and_condition,
      has_required_fields,
      has_discount,
      sold_quantity,
      props.is_favorite,
      slug,
      props.unit_id,
    ],
  );

  return (
    <ProductCard
      size={size}
      theme={theme}
      scrollableTabs={scrollableTabs}
      isGrid={isGrid}
      units={props.units}
      type={props.type}
      bundleSetSelectedChildIndex={props.bundleSetSelectedChildIndex}
      preview_type={props.preview_type as ProductPreviewTypes}
      minimizedProduct={minimizedProduct}
      priority={priority}
      productName={name}
    >
      <ProductClick slug={slug} isEvent={scrollableTabs} Customize={Customize}>
        <ProductBoxImage
          theme={theme}
          scrollableTabs={scrollableTabs}
          size={size}
          priority={priority}
          tagIds={tag_ids}
          productId={props.id}
        />
      </ProductClick>
      <div className="info-container">
        <div className="product-info">
          {show_name && (
            <ProductClick slug={slug} isEvent={scrollableTabs}>
              <ProductName name={name} isEvent={preview_type === "event"} />
            </ProductClick>
          )}
          {preview_type === "event" && showCustomEventData && (
            <EventCustomData
              {...custom_data!}
              price={price}
              price_without_discount={price_without_discount}
              discount_amount={discount_amount}
              discount_type={discount_type}
              discount={discount}
              special_prices={selling_prices}
            />
          )}
          {show_description && (
            <ProductDescription size={size} description={description ?? ""} />
          )}
          {(show_brand || show_low_stock_alert) && (
            <div className="product-brand-containter">
              {show_brand && <ProductBrand size={size} brand={brand} />}
              {showMinStockQuantityMsg && (
                <StockAlertMessage minStockQuantity={quantity_on_hand} />
              )}
            </div>
          )}
        </div>
        <div className="product-actions">
          <ProductBoxTailAndRate {...props} />
        </div>
      </div>
    </ProductCard>
  );
};

export default memo(ProductBox);
